import type { ReactNode } from 'react';
import React, { useCallback, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import Typography, { TypographyVariant } from '../Typography';
import type { ITheme, ThemeColor } from '../../styles/theme';
import Accordion from '.';
import type { IAccordion } from './Accordion';
import type { IPropsWithTestId } from '../../../constants/data-element-tracking-ids';

type AccordionLightProps = IPropsWithTestId &
Pick<IAccordion, 'expandIconPosition'> & {
  title: ReactNode;
  titleTypography?: TypographyVariant;
  details: ReactNode;
  detailsTypography?: TypographyVariant;
  expandIconColor?: ThemeColor;
  isWithDynamicGaps?: boolean;
  isWithIconSpace?: boolean;
  theme: ITheme;
};

export const AccordionLight: React.FC<AccordionLightProps> = ({
  title,
  titleTypography = TypographyVariant.MEDIUM_BODY_BOLD,
  details,
  detailsTypography = TypographyVariant.MEDIUM_BODY_REGULAR,
  theme,
  expandIconPosition = 'end',
  expandIconColor = theme.colors.secondary,
  isWithIconSpace = false,
  isWithDynamicGaps = false,
  dataElementId,
}) => {
  const [isActiveItem, setActiveItem] = useState(false);

  const handleOnClick = useCallback(
    () => setActiveItem((activeItem) => !activeItem),
    [setActiveItem],
  );

  return (
    <Accordion
      dataElementId={dataElementId}
      expanded={isActiveItem}
      onChange={handleOnClick}
      clickableDetails
      summaryContents={
        <Typography variant={titleTypography}>{title}</Typography>
      }
      expandIconPosition={expandIconPosition}
      expandIcon={(
        <FontAwesomeIcon
          icon={faChevronDown}
          style={{ color: expandIconColor }}
        />
      )}
      TransitionProps={{ timeout: 700 }}
      theme={theme}
      additionalStyles={{
        boxShadow: 'none',
        backgroundColor: 'inherit',
        '&:before': {
          backgroundColor: 'transparent !important',
        },
        ...(isWithDynamicGaps
          ? {
            marginBottom: '40px',
          }
          : {
            '&.Mui-expanded': {
              margin: 0,
            },
          }),
        '& .MuiAccordionSummary-root': {
          padding: '0',
          ...(expandIconPosition === 'end' && !isWithIconSpace
            ? {
              '& .MuiAccordionSummary-content': {
                flexGrow: 0,
              },
              justifyContent: 'flex-start',
            }
            : {}),
        },
        '& .MuiAccordionDetails-root': {
          ...(isWithDynamicGaps
            ? {
              paddingTop: '16px',
              paddingBottom: '25px',
            }
            : {
              paddingTop: '8px',
              paddingBottom: 0,
            }),
          ...(expandIconPosition === 'end'
            ? {
              paddingLeft: 0,
            }
            : {}),
        },
      }}
    >
      <Typography
        variant={detailsTypography}
        additionalStyles={{
          transition: 'opacity 1s ease-in 0s',
          opacity: isActiveItem ? '1' : '0.1',
        }}
      >
        {details}
      </Typography>
    </Accordion>
  );
};
